import type { Row } from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { History } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/lib/utils'
import {
  DataTableColumnHeader,
  DataTablePagination,
  OrganizationUsersTableToolbar,
} from '@/components/data-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  OrganizationActivityActorCell,
  OrganizationActivityDescriptionCell,
  OrganizationActivityWhenCell,
} from '@/features/organization-users/components/organization-activity-table-cells'
import type { OrganizationAuditLog } from '@/features/organization-users/types/audit-log'

const columnHelper = createColumnHelper<OrganizationAuditLog>()

function organizationActivityGlobalFilterFn(
  row: Row<OrganizationAuditLog>,
  _columnId: string,
  filterValue: unknown
): boolean {
  const query = String(filterValue ?? '')
    .trim()
    .toLowerCase()
  if (!query) {
    return true
  }
  const log = row.original
  const haystack = [
    log.eventType,
    log.resourceName,
    log.resourceId,
    ...Object.values(log.metadata),
    log.user?.firstName,
    log.user?.lastName,
    log.user?.email,
  ]
    .map((part) => String(part ?? '').toLowerCase())
    .join(' ')
  return haystack.includes(query)
}

type OrganizationActivityTableProps = {
  data: OrganizationAuditLog[]
}

export function OrganizationActivityTable({ data }: OrganizationActivityTableProps) {
  const { t } = useTranslation('organizationUsers')
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'createdAt', desc: true },
  ])
  const [globalFilter, setGlobalFilter] = useState('')

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.eventType, {
        id: 'eventType',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('activities.table.activity')}
          />
        ),
        cell: ({ row }) => (
          <OrganizationActivityDescriptionCell activity={row.original} />
        ),
        sortingFn: 'alphanumeric',
      }),
      columnHelper.accessor(
        (row) =>
          `${row.user?.firstName ?? ''} ${row.user?.lastName ?? ''}`.trim(),
        {
          id: 'initiatedBy',
          header: ({ column }) => (
            <DataTableColumnHeader
              column={column}
              title={t('activities.table.initiatedBy')}
            />
          ),
          cell: ({ row }) => (
            <OrganizationActivityActorCell activity={row.original} />
          ),
          sortingFn: 'alphanumeric',
        }
      ),
      columnHelper.accessor((row) => row.createdAt, {
        id: 'createdAt',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('activities.table.when')}
          />
        ),
        cell: ({ row }) => (
          <OrganizationActivityWhenCell activity={row.original} />
        ),
        sortingFn: 'alphanumeric',
      }),
    ],
    [t]
  )

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: organizationActivityGlobalFilterFn,
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      pagination: { pageSize: 20, pageIndex: 0 },
    },
  })

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 rounded-md border border-dashed py-16">
        <History className="size-10 text-muted-foreground/50" />
        <div className="space-y-1 text-center">
          <h3 className="text-lg font-semibold">
            {t('activities.emptyState.title')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t('activities.emptyState.description')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'max-sm:has-[div[role=toolbar]]:mb-16',
        'flex flex-col gap-4'
      )}
    >
      <div role="toolbar" className="flex flex-col gap-2">
        <OrganizationUsersTableToolbar
          table={table}
          searchPlaceholder={t('activities.searchPlaceholder')}
          resetLabel={t('sections.usersList.resetFilters')}
        />
      </div>
      <div className="overflow-hidden rounded-md border">
        <Table className="table-fixed">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="group/row">
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    className={cn(
                      'bg-background group-hover/row:bg-muted group-data-[state=selected]/row:bg-muted',
                      header.column.id === 'eventType' &&
                        'w-[52%] min-w-0 max-w-[52%]',
                      header.column.id === 'initiatedBy' &&
                        'w-[28%] min-w-0 max-w-[28%]',
                      header.column.id === 'createdAt' &&
                        'w-[20%] min-w-0 max-w-[20%]'
                    )}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className="group/row">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        'bg-background group-hover/row:bg-muted align-top',
                        cell.column.id === 'eventType' &&
                          'w-[52%] min-w-0 max-w-[52%] whitespace-normal',
                        cell.column.id === 'initiatedBy' &&
                          'w-[28%] min-w-0 max-w-[28%]',
                        cell.column.id === 'createdAt' &&
                          'w-[20%] min-w-0 max-w-[20%] whitespace-normal'
                      )}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  {t('activities.noResults')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DataTablePagination
        table={table}
        className="mt-auto"
        rowsPerPageLabel={t('sections.usersList.rowsPerPage')}
        pageLabel={(current, total) =>
          t('sections.usersList.pageStatus', { current, total })
        }
      />
    </div>
  )
}
